import React from "react";
import { DropdownNode } from "./allData";
import {
  ChevronDown,
  ChevronUp,
  CirclePlus,
  Copy,
  Delete,
  Trash,
  CheckCheck,
  RotateCw,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Button } from "@components/ui/button";
import InfoTooltip from "@components/common/InfoTooltip";
import { toast } from "@components/ui/use-toast";
import { useMutation } from "react-query";
import { executeWorkflow } from "@pages/CreatePlugin/createPluginApis";
import PageLoader from "@components/loaders/PageLoader";

interface DropdownNodeProps {
  node: DropdownNode;
  onUpdate: (updated: DropdownNode) => void;
  onDelete: () => void;
  actionBundles: any[];
  definedVariables: any[] | undefined;
  extractKeysFromData: (data: any) => string[];
  generateDropdownOptions: (
    raw: any,
    labelKey: string,
    valueKey: string,
  ) => { label: string; value: string }[];
  prefillData?: any;
  applicableOptions?: any[];
  meshVersion?: string;
  pluginId?: string;
  isNested?: boolean; // Indicates if this node is nested/from preview options
}

const DropdownNodeEditor: React.FC<DropdownNodeProps> = ({
  node,
  onUpdate,
  onDelete,
  actionBundles,
  definedVariables,
  extractKeysFromData,
  generateDropdownOptions,
  prefillData,
  meshVersion = "1.0",
  pluginId = "",
  isNested = false,
}) => {
  // Get pluginId from session storage (matching CreatePlugin pattern)
  const activePluginId = React.useMemo(() => {
    return (
      pluginId ||
      sessionStorage.getItem("meshPluginId") ||
      sessionStorage.getItem("editPluginId") ||
      ""
    );
  }, [pluginId]);

  const [bundleId, setBundleId] = React.useState(node.bundleId || "");
  const [variableName, setVariableName] = React.useState(
    node.variableName || "",
  );
  const [labelKey, setLabelKey] = React.useState(node.labelKey || "");
  const [valueKey, setValueKey] = React.useState(node.valueKey || "");
  const [filteredOptions, setFilteredOptions] = React.useState<
    { label: string; value: string }[]
  >([]);

  const [expandedTargetFields, setExpandedTargetFields] = React.useState<
    Set<number>
  >(new Set());

  const hydrated = React.useRef(false);
  const hasInitialized = React.useRef(false);
  const isRestoringWorkflowState = React.useRef(false);

  const [targetFields, setTargetFields] = React.useState<any[]>(
    node.targetFields || [],
  );

  // Workflow data state for mesh version 2.0
  const [workflowData, setWorkflowData] = React.useState<any>(null);
  const [selectedDataIndex, setSelectedDataIndex] = React.useState<string>("");
  const [selectedNodeId, setSelectedNodeId] = React.useState<string>("");
  const [workflowNodeResults, setWorkflowNodeResults] = React.useState<any[]>(
    [],
  );
  const [loadingFieldIndex, setLoadingFieldIndex] = React.useState<
    number | null
  >(null);

  // Mutation for executing workflow (mesh version 2.0)
  const executeWorkflowMutation = useMutation(executeWorkflow, {
    onSuccess: (response) => {
      // Clear loading state
      setLoadingFieldIndex(null);

      // Check if workflow execution succeeded
      const isSuccess = response?.success && response?.status === "success";
      const workflowResult = response?.results?.[0];

      if (isSuccess && workflowResult?.status === "success") {
        if (workflowResult?.node_results) {
          const nodeResults = workflowResult.node_results;

          // Store all node results for user selection
          setWorkflowNodeResults(nodeResults);

          // Don't reset selections if we're restoring from saved state
          if (!isRestoringWorkflowState.current) {
            setSelectedNodeId(""); // Reset node selection
            setWorkflowData(null); // Reset data until node is selected
            setSelectedDataIndex(""); // Reset data index
          }
          // If restoring, the state is already set, just clear the flag
          // The workflowData will be set by the useEffect that watches selectedNodeId

          toast({
            title: "Workflow Executed",
            description: "Please select a node to view its data.",
            variant: "success",
            duration: 2000,
          });
          return;
        }
      }

      // If we get here, something went wrong
      console.warn("No data in workflow response");
      toast({
        title: "No Data",
        description: "Workflow executed but returned no data",
        variant: "destructive",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      console.error("Workflow execution error:", error);
      toast({
        title: "Workflow Execution Failed",
        description: error?.message || "Failed to execute workflow",
        variant: "destructive",
        duration: 3000,
      });
    },
  });

  const sync = (patch = {}) => {
    const updatedNode = {
      ...node,
      label: node.label,
      value: node.value,
      targetFields: targetFields.map((tf: any) => ({
        ...tf,
        givenName: tf.fieldName
          ? tf.fieldName.toLowerCase().replace(/\s+/g, "_")
          : "",
      })),
      children: node.children || [],
      ...patch,
    };

    onUpdate(updatedNode);
  };

  // Update applyNodeUpdate to preserve children
  const applyNodeUpdate = (changes: Partial<DropdownNode>) => {
    onUpdate({
      ...node,
      children: node.children || [],
      ...changes,
    });
  };

  // Initialize from node prop on first load
  React.useEffect(() => {
    if (node && !hasInitialized.current) {
      hasInitialized.current = true;

      //  Initialize target fields from node
      if (node.targetFields && Array.isArray(node.targetFields)) {
        setTargetFields(node.targetFields);
      }

      //  Handle children - this is the critical part
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        // Check if children are already in the correct format or need processing
        const processedChildren = node.children.map((child: any) => {
          // If child already has the full structure, use it
          if (child.label && child.value) {
            return child;
          }
          // Otherwise, it might be a reference that needs to be resolved
          return child;
        });

        if (node.isDefaultOption) {
          setFilteredOptions([]);
        } else {
          setFilteredOptions(processedChildren);
        }
      } else {
        setFilteredOptions([]);
      }
    }
  }, [node, definedVariables]);

  // Reset initialization when node changes
  React.useEffect(() => {
    hasInitialized.current = false;
  }, [node?.value, node?.label, node?.bundleId]);

  // Hydrate from prefillData on first load (edit mode)
  React.useEffect(() => {
    if (!prefillData || hydrated.current) return;
    hydrated.current = true;

    // 1. Hydrate basic state
    setBundleId(prefillData.bundleId || "");
    setVariableName(prefillData.variableName || "");
    setLabelKey(prefillData.labelKey || "");
    setValueKey(prefillData.valueKey || "");

    // 2. Restore children with null safety
    if (prefillData.isDefaultOption) {
      setFilteredOptions([]);
    } else {
      const children =
        prefillData.children === null ? [] : prefillData.children || [];
      setFilteredOptions(children);
    }

    // 3. Recursively restore target fields with null safety
    if (prefillData.targetFields && Array.isArray(prefillData.targetFields)) {
      const restorePreviewOptions = (
        options: any[],
        depth: number = 0,
      ): any[] => {
        if (!options || !Array.isArray(options)) {
          return [];
        }

        return options.map((opt: any) => {
          const restoredOpt = {
            ...opt,
            label: opt.label || "",
            value: opt.value || "",
            children: opt.children === null ? [] : opt.children || [],
          };

          const targetFields =
            opt.targetFields === null ? [] : opt.targetFields || [];

          if (Array.isArray(targetFields) && targetFields.length > 0) {
            restoredOpt.targetFields = targetFields.map((nestedTf: any) => {
              return {
                ...nestedTf,
                fieldName: nestedTf.fieldName || "",
                givenName:
                  nestedTf.givenName ||
                  (nestedTf.fieldName
                    ? nestedTf.fieldName.toLowerCase().replace(/\s+/g, "_")
                    : ""),
                sourceType: nestedTf.sourceType || "bundle",
                bundleId: nestedTf.bundleId || "",
                variableName: nestedTf.variableName || "",
                labelKey: nestedTf.labelKey || "",
                valueKey: nestedTf.valueKey || "",
                availableKeys: nestedTf.availableKeys || [],
                defaultOptions: nestedTf.defaultOptions || [],
                selected_item: nestedTf.selected_item || "", // Restore for nested fields
                previewOptions: restorePreviewOptions(
                  nestedTf.previewOptions === null
                    ? []
                    : nestedTf.previewOptions || [],
                  depth + 1,
                ),
                commonApiConfig: nestedTf.commonApiConfig || undefined,
              };
            });
          } else {
            restoredOpt.targetFields = [];
          }

          return restoredOpt;
        });
      };

      const restoredTargetFields = prefillData.targetFields.map((tf: any) => {
        const restored = {
          ...tf,
          fieldName: tf.fieldName || "",
          givenName:
            tf.givenName ||
            (tf.fieldName
              ? tf.fieldName.toLowerCase().replace(/\s+/g, "_")
              : ""),
          sourceType: tf.sourceType || "bundle",
          bundleId: tf.bundleId || "",
          variableName: tf.variableName || "",
          labelKey: tf.labelKey || "",
          valueKey: tf.valueKey || "",
          availableKeys: tf.availableKeys || [],
          defaultOptions: tf.defaultOptions || [],
          selected_item: tf.selected_item || "", // Restore selected data item for v2.0
          previewOptions: restorePreviewOptions(
            tf.previewOptions === null ? [] : tf.previewOptions || [],
            0,
          ),
          commonApiConfig: tf.commonApiConfig || undefined,
        };

        return restored;
      });

      setTargetFields(restoredTargetFields);

      //  4.  Properly restore commonApiCallEnabled state AND auto-expand
      const fieldsToExpand = new Set<number>();
      const commonApiStates: Record<number, boolean> = {};

      //  Helper to check if field has ACTUAL nested targetFields with fieldNames
      const hasActualNestedData = (field: any): boolean => {
        if (!field.previewOptions || field.previewOptions.length === 0) {
          return false;
        }

        for (const opt of field.previewOptions) {
          if (opt.targetFields && Array.isArray(opt.targetFields)) {
            for (const nestedTf of opt.targetFields) {
              if (nestedTf.fieldName && nestedTf.fieldName.trim() !== "") {
                return true;
              }
            }
          }
        }
        return false;
      };

      //  Helper to check if field uses common API (all options have same targetFields)
      const usesCommonApi = (field: any): boolean => {
        // Check if commonApiConfig exists with valid data
        if (
          field.commonApiConfig &&
          field.commonApiConfig.targetFields?.length > 0
        ) {
          const hasValidCommonTargets = field.commonApiConfig.targetFields.some(
            (t: any) => t.fieldName && t.fieldName.trim() !== "",
          );
          if (hasValidCommonTargets) {
            return true;
          }
        }

        //  Also check if all preview options have identical targetFields (common mode)
        if (field.previewOptions && field.previewOptions.length > 0) {
          const firstOptionTargets = field.previewOptions[0]?.targetFields;

          if (!firstOptionTargets || firstOptionTargets.length === 0) {
            return false;
          }

          // Check if first option has valid targetFields
          const firstHasValidTargets = firstOptionTargets.some(
            (tf: any) => tf.fieldName && tf.fieldName.trim() !== "",
          );

          if (!firstHasValidTargets) {
            return false;
          }

          // Check if all options have the same targetFields structure (common config indicator)
          const allSame = field.previewOptions.every((opt: any) => {
            if (!opt.targetFields) return false;

            // Check if targetFields match in length and fieldNames
            if (opt.targetFields.length !== firstOptionTargets.length)
              return false;

            return opt.targetFields.every((tf: any, tfIdx: number) => {
              return tf.fieldName === firstOptionTargets[tfIdx]?.fieldName;
            });
          });

          return allSame;
        }

        return false;
      };

      restoredTargetFields.forEach((tf: any, idx: number) => {
        //  Check if this field uses common API configuration
        const isCommonApi = usesCommonApi(tf);

        if (isCommonApi) {
          commonApiStates[idx] = true;
          fieldsToExpand.add(idx);

          //   Store the common config from first option if not already present
          if (!tf.commonApiConfig && tf.previewOptions?.[0]?.targetFields) {
            tf.commonApiConfig = {
              targetFields: tf.previewOptions[0].targetFields,
            };
          }
        } else if (hasActualNestedData(tf)) {
          // Individual configurations
          commonApiStates[idx] = false;
          fieldsToExpand.add(idx);
        }
      });

      setExpandedTargetFields(fieldsToExpand);
      setCommonApiCallEnabled(commonApiStates);

      // 4b. Restore workflow state for mesh 2.0 without executing workflow
      if (meshVersion === "2.0" && restoredTargetFields.length > 0) {
        // Find first field with workflow data (bundleId starting with "workflow_")
        const workflowField = restoredTargetFields.find(
          (tf: any) => tf.bundleId && tf.bundleId.startsWith("workflow_"),
        );

        if (workflowField) {
          // Set flag to prevent resets during state restoration
          isRestoringWorkflowState.current = true;

          // Restore selectedNodeId from variableName
          if (workflowField.variableName) {
            setSelectedNodeId(workflowField.variableName);
          }

          // Restore selectedDataIndex from selected_item
          if (workflowField.selected_item) {
            setSelectedDataIndex(workflowField.selected_item);
          }

          // Check if we have complete saved data with previewOptions
          const hasCompleteData =
            workflowField.previewOptions &&
            Array.isArray(workflowField.previewOptions) &&
            workflowField.previewOptions.length > 0;

          if (hasCompleteData) {
            // Find the workflow to get node details
            const workflow = actionBundles.find(
              (b: any) => b.bundleId === workflowField.bundleId,
            );

            // Get node title from workflow nodes array
            let nodeTitle = "Restored Node";
            let nodeLabel = "Restored";

            if (workflow && workflow.workflow && workflow.workflow.nodes) {
              const savedNode = workflow.workflow.nodes.find(
                (n: any) => n.uid === workflowField.variableName,
              );
              if (savedNode) {
                nodeTitle =
                  savedNode.title || savedNode.label || "Restored Node";
                nodeLabel = savedNode.label || savedNode.title || "Restored";
              }
            }

            // Reconstruct workflowData from previewOptions
            // The previewOptions contain the data that was available when initially configured
            const reconstructedData = workflowField.previewOptions.map(
              (opt: any) => ({
                [workflowField.labelKey]: opt.label,
                [workflowField.valueKey]: opt.value,
              }),
            );

            setWorkflowData(reconstructedData);

            // Create a fake node result to populate the node dropdown with actual title
            const fakeNodeResult = {
              uid: workflowField.variableName,
              title: nodeTitle,
              header: { label: nodeLabel },
              status: "success",
              sub_type: "restored",
              output_data: [
                {
                  json_data: reconstructedData,
                },
              ],
            };

            setWorkflowNodeResults([fakeNodeResult]);

            // Clear the restoring flag
            setTimeout(() => {
              isRestoringWorkflowState.current = false;
            }, 100);
          } else {
            // No complete data saved, execute workflow to get fresh data
            const workflow = actionBundles.find(
              (b: any) => b.bundleId === workflowField.bundleId,
            );

            if (workflow && workflow.workflow) {
              const executionPayload = {
                workflowData: {
                  uid: workflow.workflow.uid || "",
                  title:
                    workflow.workflow.title ||
                    workflow.workflow.workflow_name ||
                    "Execute Workflow",
                  workflow_id: workflowField.bundleId,
                  plugin_id:
                    workflow.workflow.plugin_id ||
                    sessionStorage.getItem("meshPluginId") ||
                    sessionStorage.getItem("editPluginId") ||
                    "",
                  workflow_name:
                    workflow.workflow.title ||
                    workflow.workflow.workflow_name ||
                    "Execute Workflow",
                  is_test_connection: false,
                  type_workflow: workflow.workflow.type_workflow || "action",
                  cron_run_time: workflow.workflow.cron_run_time || null,
                  dependency_workflow:
                    workflow.workflow.dependency_workflow || [],
                  is_active: workflow.workflow.is_active ?? true,
                  description: workflow.workflow.description || null,
                  version: workflow.workflow.version || "1.0",
                  threatlookup_data: workflow.workflow.threatlookup_data || {},
                  threat_lookup_search_types:
                    workflow.workflow.threat_lookup_search_types || [],
                  nodes: workflow.workflow.nodes || [],
                  connections: workflow.workflow.connections || {},
                },
                plugin_id:
                  workflow.workflow.plugin_id ||
                  sessionStorage.getItem("meshPluginId") ||
                  sessionStorage.getItem("editPluginId") ||
                  "",
                testData: {},
              };

              // Execute workflow to get node results
              setTimeout(() => {
                executeWorkflowMutation.mutate(executionPayload);
              }, 100);
            }
          }
        }
      }
    }

    // 5. Restore node WITH children (null-safe)
    applyNodeUpdate({
      ...node,
      ...prefillData,
      children: prefillData.children === null ? [] : prefillData.children || [],
      targetFields:
        prefillData.targetFields === null ? [] : prefillData.targetFields || [],
    });
  }, [prefillData, meshVersion, actionBundles, executeWorkflowMutation]);

  //  Auto-expand fields when they get preview options (during editing/creation)
  React.useEffect(() => {
    //   Skip if we're in hydration mode (editing existing data)
    if (hydrated.current) {
      return;
    }

    const fieldsWithOptions = new Set<number>();

    targetFields.forEach((field: any, idx: number) => {
      //   Check if any preview option has ACTUAL nested targetFields with fieldNames
      if (field.previewOptions && Array.isArray(field.previewOptions)) {
        const hasValidNested = field.previewOptions.some((opt: any) => {
          if (!opt.targetFields || !Array.isArray(opt.targetFields)) {
            return false;
          }
          // Return true if at least one targetField has a non-empty fieldName
          return opt.targetFields.some(
            (tf: any) => tf.fieldName && tf.fieldName.trim() !== "",
          );
        });

        if (hasValidNested) {
          fieldsWithOptions.add(idx);
        }
      }

      //  Check for common API config with valid targets
      if (
        field.commonApiConfig &&
        field.commonApiConfig.targetFields?.length > 0
      ) {
        const hasValidCommonTargets = field.commonApiConfig.targetFields.some(
          (t: any) => t.fieldName && t.fieldName.trim() !== "",
        );

        if (hasValidCommonTargets) {
          fieldsWithOptions.add(idx);
        }
      }
    });

    if (fieldsWithOptions.size > 0) {
      setExpandedTargetFields((prev) => {
        const newSet = new Set([...prev, ...fieldsWithOptions]);
        return newSet;
      });
    }
  }, [targetFields]);

  //  Reset hydrated when node/prefillData changes
  React.useEffect(() => {
    hydrated.current = false;
  }, [prefillData?.value, prefillData?.label, prefillData?.bundleId]);

  React.useEffect(() => {
    if (!bundleId || !variableName) return;

    const variable = definedVariables?.find(
      (v) => String(v.sourceBundleId) === bundleId && v.name === variableName,
    );

    if (!variable) return;

    const raw = variable.value;

    // Single primitive array → direct dropdown
    if (
      Array.isArray(raw) &&
      raw.length &&
      (typeof raw[0] === "string" ||
        typeof raw[0] === "number" ||
        typeof raw[0] === "boolean")
    ) {
      const opts = raw.map((v) => ({ label: String(v), value: String(v) }));
      setFilteredOptions(opts);
      applyNodeUpdate({ children: opts });
      return;
    }

    // Array of objects → use keys
    if (labelKey && valueKey) {
      const opts = generateDropdownOptions(raw, labelKey, valueKey);
      setFilteredOptions(opts);
      applyNodeUpdate({ children: opts });
    }
  }, [bundleId, variableName, labelKey, valueKey, definedVariables]);

  // Auto-update filteredOptions when bundle/variable/keys change
  React.useEffect(() => {
    if (!bundleId || !variableName || !labelKey || !valueKey) {
      setFilteredOptions([]);
      return;
    }

    //  Skip if options are already hydrated (edit mode)
    if (
      filteredOptions.length > 0 &&
      node.children?.length === filteredOptions.length
    ) {
      return;
    }

    const variable = definedVariables?.find(
      (v) => String(v.sourceBundleId) === bundleId && v.name === variableName,
    );
    if (!variable) {
      setFilteredOptions([]);
      return;
    }

    try {
      const raw = variable.value;

      let opts = [];

      if (
        Array.isArray(raw) &&
        raw.length > 0 &&
        (typeof raw[0] === "string" ||
          typeof raw[0] === "number" ||
          typeof raw[0] === "boolean")
      ) {
        opts = raw.map((item) => ({
          label: String(item),
          value: String(item),
        }));
      } else {
        opts = generateDropdownOptions(raw, labelKey, valueKey);
      }

      setFilteredOptions(opts);
      applyNodeUpdate({
        ...node,
        children: opts.map((opt) => ({
          label: opt.label,
          value: opt.value,
        })),
      });
    } catch {
      setFilteredOptions([]);
    }
  }, [bundleId, variableName, labelKey, valueKey, definedVariables]);

  React.useEffect(() => {
    if (!bundleId || !labelKey || !valueKey) return;

    const variable = definedVariables?.find(
      (v) => String(v.sourceBundleId) === bundleId && v.name === variableName,
    );

    if (!variable) return;

    const raw = variable.value;

    if (
      Array.isArray(raw) &&
      raw.length > 0 &&
      (typeof raw[0] === "string" ||
        typeof raw[0] === "number" ||
        typeof raw[0] === "boolean")
    ) {
      const opts = raw.map((v) => ({ label: String(v), value: String(v) }));
      setFilteredOptions(opts);
      applyNodeUpdate({ children: opts });
      return;
    }

    const opts = generateDropdownOptions(raw, labelKey, valueKey);
    setFilteredOptions(opts);
    applyNodeUpdate({ children: opts });
  }, [bundleId, variableName, labelKey, valueKey, definedVariables]);

  // Auto-process workflowData for mesh version 2.0 when data item is selected
  React.useEffect(() => {
    if (meshVersion !== "2.0" || !workflowData || !selectedDataIndex) return;

    // Get the selected data item
    let selectedData;
    if (Array.isArray(workflowData)) {
      const index = parseInt(selectedDataIndex);
      selectedData = workflowData[index];
    } else {
      // If it's an object with keys, get the nested data
      const keys = Object.keys(workflowData);
      if (keys.length > 0) {
        selectedData = workflowData[keys[parseInt(selectedDataIndex)]];
      }
    }

    if (!selectedData) return;

    // Find the target field that just had its workflow selected
    const fieldIndexToUpdate = targetFields.findIndex(
      (field) =>
        field.bundleId &&
        field.sourceType === "bundle" &&
        (!field.availableKeys || field.availableKeys.length === 0) &&
        (!field.previewOptions || field.previewOptions.length === 0),
    );

    if (fieldIndexToUpdate === -1) return;

    // Check if selected data is empty/null/undefined
    if (
      selectedData === null ||
      selectedData === undefined ||
      selectedData === ""
    ) {
      toast({
        variant: "destructive",
        title: "Empty Data",
        description:
          "The selected data item is empty. Please select a different item.",
      });
      return;
    }

    // Handle simple array (string/number/boolean primitives)
    if (
      Array.isArray(selectedData) &&
      selectedData.length > 0 &&
      (typeof selectedData[0] === "string" ||
        typeof selectedData[0] === "number" ||
        typeof selectedData[0] === "boolean")
    ) {
      // Filter out empty values
      const filteredValues = selectedData.filter(
        (v) => v !== null && v !== undefined && v !== "",
      );

      if (filteredValues.length === 0) {
        toast({
          variant: "destructive",
          title: "No Valid Data",
          description: "The selected data contains only empty values.",
        });
        return;
      }

      // Generate preview options from simple array
      const opts = filteredValues.map((v) => ({
        label: String(v),
        value: String(v),
      }));

      updateTargetField(fieldIndexToUpdate, {
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: opts,
      });
      return;
    }

    // Handle empty array
    if (Array.isArray(selectedData) && selectedData.length === 0) {
      toast({
        variant: "destructive",
        title: "Empty Array",
        description:
          "The selected data is an empty array. Please select a different item.",
      });
      return;
    }

    // Handle single primitive value
    if (
      typeof selectedData === "string" ||
      typeof selectedData === "number" ||
      typeof selectedData === "boolean"
    ) {
      // Check if string is empty
      if (typeof selectedData === "string" && selectedData.trim() === "") {
        toast({
          variant: "destructive",
          title: "Empty Value",
          description:
            "The selected data is an empty string. Please select a different item.",
        });
        return;
      }

      const opts = [
        { label: String(selectedData), value: String(selectedData) },
      ];

      updateTargetField(fieldIndexToUpdate, {
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: opts,
      });
      return;
    }

    // Extract keys from complex data (object/array of objects)
    const keys = extractKeysFromData(selectedData);

    if (!keys || keys.length === 0) {
      toast({
        variant: "destructive",
        title: "No Extractable Keys",
        description:
          "Cannot extract keys from the selected data. The data structure may be invalid.",
      });
      return;
    }

    // Update field with available keys for user selection
    updateTargetField(fieldIndexToUpdate, {
      availableKeys: keys,
      labelKey: "",
      valueKey: "",
      previewOptions: [],
    });
  }, [selectedDataIndex, workflowData, meshVersion]);

  // Process selected node's output data
  React.useEffect(() => {
    if (meshVersion !== "2.0" || !selectedNodeId || !workflowNodeResults.length)
      return;

    // Find the selected node's result
    const selectedNode = workflowNodeResults.find(
      (nr: any) => nr.uid === selectedNodeId,
    );

    if (!selectedNode) {
      toast({
        title: "Node Not Found",
        description: "Selected node data not found",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Check if node has output_data
    if (!selectedNode.output_data || selectedNode.output_data.length === 0) {
      toast({
        title: "No Data",
        description: "Selected node has no output data",
        variant: "destructive",
        duration: 3000,
      });
      setWorkflowData(null);
      return;
    }

    // Extract and process the node's output data
    let outputData =
      selectedNode.output_data[0]?.json_data || selectedNode.output_data[0];

    // Unwrap if data is wrapped in an array with 'item' property
    // Handle format: [{ "item": { actual_data } }]
    if (
      Array.isArray(outputData) &&
      outputData.length > 0 &&
      outputData[0]?.item
    ) {
      outputData = outputData[0].item;
    }

    // Unwrap single-element array containing an object
    // Handle format: [{ todos: [...], total, skip, limit }] → { todos: [...], total, skip, limit }
    if (
      Array.isArray(outputData) &&
      outputData.length === 1 &&
      typeof outputData[0] === "object" &&
      outputData[0] !== null
    ) {
      outputData = outputData[0];
    }

    // Check if we have valid data after unwrapping
    if (
      !outputData ||
      (Array.isArray(outputData) && outputData.length === 0) ||
      (typeof outputData === "object" && Object.keys(outputData).length === 0)
    ) {
      console.warn("No valid data after unwrapping");
      toast({
        title: "No Data",
        description: "Selected node returned no usable data",
        variant: "destructive",
        duration: 3000,
      });
      setWorkflowData(null);
      return;
    }

    // Store the processed data
    setWorkflowData(outputData);

    // Don't reset selectedDataIndex if we're restoring from saved state
    if (!isRestoringWorkflowState.current) {
      setSelectedDataIndex(""); // Reset selection

      toast({
        title: "Node Data Loaded",
        description:
          "Data loaded successfully. Select a data item to continue.",
        variant: "success",
        duration: 2000,
      });
    } else {
      // Clear the restoring flag after successful restoration
      isRestoringWorkflowState.current = false;
    }
  }, [selectedNodeId, workflowNodeResults, meshVersion]);

  /** TARGET FIELDS */
  const addTargetField = () => {
    const updated = [
      ...targetFields,
      {
        fieldName: "",
        givenName: "",
        sourceType: "bundle",
        bundleId: "",
        variableName: "",
        labelKey: "",
        valueKey: "",
        availableKeys: [],
        defaultOptions: [],
        previewOptions: [],
        nodeId: "",
        nodeLabel: "",
        selected_item: "", // For v2.0 workflow data selection
      },
    ];
    setTargetFields(updated);
    sync({ targetFields: updated });
  };

  const removeTargetField = (i: number) => {
    const updated = targetFields.filter((_, idx) => idx !== i);
    setTargetFields(updated);
    sync({ targetFields: updated });
  };

  const updateTargetField = (i: number, patch: any) => {
    const updated = [...targetFields];
    // Auto-generate givenName if fieldName is being updated
    if (patch.fieldName !== undefined) {
      patch.givenName = patch.fieldName.toLowerCase().replace(/\s+/g, "_");
    }
    updated[i] = { ...updated[i], ...patch };
    setTargetFields(updated);
    sync({ targetFields: updated });
  };

  /** DEFAULT OPTIONS */
  const addDefaultOption = (fieldIndex: number, opt: any) => {
    const updated = [...targetFields];
    if (!updated[fieldIndex].defaultOptions) {
      updated[fieldIndex].defaultOptions = [];
    }

    // Check for duplicates
    if (
      updated[fieldIndex].defaultOptions.some(
        (existing: any) =>
          existing.label === opt.label || existing.value === opt.value,
      )
    ) {
      toast({
        variant: "destructive",
        title: "Duplicate Option",
        description: "This option already exists.",
      });
      return;
    }

    updated[fieldIndex].defaultOptions.push(opt);

    //  Also add to preview options
    if (!updated[fieldIndex].previewOptions) {
      updated[fieldIndex].previewOptions = [];
    }
    updated[fieldIndex].previewOptions.push(opt);

    setTargetFields(updated);
    sync({ targetFields: updated });
  };

  //   Delete option from preview (for both bundle and default options)
  const handleDeletePreviewOption = (fieldIndex: number, optIndex: number) => {
    const updated = [...targetFields];
    const removedOption = updated[fieldIndex].previewOptions[optIndex];

    // Remove from preview
    updated[fieldIndex].previewOptions.splice(optIndex, 1);

    // If it's a default option, also remove from defaultOptions
    if (updated[fieldIndex].defaultOptions) {
      updated[fieldIndex].defaultOptions = updated[
        fieldIndex
      ].defaultOptions.filter(
        (opt: any) =>
          !(
            opt.label === removedOption.label &&
            opt.value === removedOption.value
          ),
      );
    }

    setTargetFields(updated);
    sync({ targetFields: updated });
  };

  //   Handle bundle/workflow change for specific target field
  const handleTargetFieldBundleChange = (
    fieldIndex: number,
    bundleId: string,
  ) => {
    if (bundleId == "none") {
      updateTargetField(fieldIndex, {
        bundleId: "",
        variableName: "",
        labelKey: "",
        valueKey: "",
        availableKeys: [],
        previewOptions: [],
      });
      // Reset workflow data states
      setWorkflowData(null);
      setSelectedDataIndex("");
      setSelectedNodeId("");
      setWorkflowNodeResults([]);
      return;
    }

    updateTargetField(fieldIndex, {
      bundleId,
      variableName: "",
      labelKey: "",
      valueKey: "",
      availableKeys: [],
      previewOptions: [],
    });

    // Reset workflow data states when changing workflow
    setWorkflowData(null);
    setSelectedDataIndex("");
    setSelectedNodeId("");
    setWorkflowNodeResults([]);

    // For mesh version 2.0, execute workflow immediately
    if (meshVersion === "2.0") {
      const selectedWorkflow = actionBundles?.find(
        (b) => String(b.bundleId) === bundleId,
      );

      if (selectedWorkflow?.workflow) {
        const workflow = selectedWorkflow.workflow;
        const workflowId = workflow.workflow_id || workflow.uid || "";

        const executionPayload = {
          workflowData: {
            uid: workflow.uid || "",
            title:
              workflow.title || workflow.workflow_name || "Execute Workflow",
            workflow_id: workflowId,
            plugin_id: workflow.plugin_id || activePluginId,
            workflow_name:
              workflow.title || workflow.workflow_name || "Execute Workflow",
            is_test_connection: false,
            type_workflow: workflow.type_workflow || "action",
            cron_run_time: workflow.cron_run_time || null,
            dependency_workflow: workflow.dependency_workflow || [],
            is_active: workflow.is_active ?? true,
            description: workflow.description || null,
            version: workflow.version || "1.0",
            threatlookup_data: workflow.threatlookup_data || {},
            threat_lookup_search_types:
              workflow.threat_lookup_search_types || [],
            nodes: workflow.nodes || [],
            connections: workflow.connections || {},
          },
          plugin_id: workflow.plugin_id || activePluginId,
          testData: {},
        };

        // Set loading state for this specific field
        setLoadingFieldIndex(fieldIndex);
        executeWorkflowMutation.mutate(executionPayload);
      } else {
        console.warn("No workflow found for bundleId:", bundleId);
        console.warn("Available bundles:", actionBundles);
        toast({
          title: "Workflow Not Found",
          description: "The selected workflow could not be found",
          variant: "destructive",
          duration: 3000,
        });
      }
    }
  };

  //   Handle variable change for specific target field
  const handleTargetFieldVariableChange = (
    fieldIndex: number,
    variableName: string,
  ) => {
    const field = targetFields[fieldIndex];
    const variable = definedVariables?.find(
      (v: any) =>
        String(v.sourceBundleId) === String(field.bundleId) &&
        v.name === variableName,
    );

    if (!variable) {
      toast({
        variant: "destructive",
        title: "Variable Not Found",
        description: "Selected variable does not exist.",
      });
      updateTargetField(fieldIndex, {
        variableName: "",
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: [],
      });
      return;
    }

    const value = variable.value;

    // Check if variable value is empty/null/undefined
    if (value === null || value === undefined || value === "") {
      toast({
        variant: "destructive",
        title: "Empty Variable",
        description: `The variable "${variableName}" has no data. Please refresh the variable or choose a different one.`,
      });
      updateTargetField(fieldIndex, {
        variableName: "",
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: [],
      });
      return;
    }

    //  Handle simple array (string/number/boolean)
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      (typeof value[0] === "string" ||
        typeof value[0] === "number" ||
        typeof value[0] === "boolean")
    ) {
      // Filter out empty values
      const filteredValues = value.filter(
        (v) => v !== null && v !== undefined && v !== "",
      );

      if (filteredValues.length === 0) {
        toast({
          variant: "destructive",
          title: "No Valid Data",
          description: `The variable "${variableName}" contains only empty values. Please select a variable with valid data.`,
        });
        updateTargetField(fieldIndex, {
          variableName: "",
          availableKeys: [],
          labelKey: "",
          valueKey: "",
          previewOptions: [],
        });
        return;
      }

      // Generate preview options from simple array
      const opts = filteredValues.map((v) => ({
        label: String(v),
        value: String(v),
      }));

      updateTargetField(fieldIndex, {
        variableName,
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: opts,
      });
      return;
    }
    // Handle empty array
    else if (Array.isArray(value) && value.length === 0) {
      toast({
        variant: "destructive",
        title: "Empty Array",
        description: `The variable "${variableName}" is an empty array. Please select a variable with data.`,
      });
      updateTargetField(fieldIndex, {
        variableName: "",
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: [],
      });
      return;
    }
    //  Handle single primitive value
    else if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      // Check if string is empty
      if (typeof value === "string" && value.trim() === "") {
        toast({
          variant: "destructive",
          title: "Empty Value",
          description: `The variable "${variableName}" is an empty string. Please select a variable with valid data.`,
        });
        updateTargetField(fieldIndex, {
          variableName: "",
          availableKeys: [],
          labelKey: "",
          valueKey: "",
          previewOptions: [],
        });
        return;
      }

      const opts = [{ label: String(value), value: String(value) }];

      updateTargetField(fieldIndex, {
        variableName,
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: opts,
      });
      return;
    }

    //  Extract keys from object/array of objects
    const keys = extractKeysFromData(value);

    if (!keys || keys.length === 0) {
      toast({
        variant: "destructive",
        title: "No Extractable Keys",
        description: `Cannot extract keys from the variable "${variableName}". The data structure may be invalid.`,
      });
      updateTargetField(fieldIndex, {
        variableName: "",
        availableKeys: [],
        labelKey: "",
        valueKey: "",
        previewOptions: [],
      });
      return;
    }

    updateTargetField(fieldIndex, {
      variableName,
      availableKeys: keys,
      labelKey: "",
      valueKey: "",
      previewOptions: [],
    });
  };

  //   Handle label key change for specific target field
  const handleTargetFieldLabelKeyChange = (
    fieldIndex: number,
    labelKey: string,
  ) => {
    const field = targetFields[fieldIndex];

    // For mesh version 2.0, use workflowData
    if (meshVersion === "2.0") {
      if (
        !field.bundleId ||
        !workflowData ||
        !selectedDataIndex ||
        !field.valueKey
      ) {
        updateTargetField(fieldIndex, { labelKey });
        return;
      }

      // Get the selected data item
      let selectedData;
      if (Array.isArray(workflowData)) {
        selectedData = workflowData[parseInt(selectedDataIndex)];
      } else {
        const keys = Object.keys(workflowData);
        selectedData = workflowData[keys[parseInt(selectedDataIndex)]];
      }

      const opts = generateDropdownOptions(
        selectedData,
        labelKey,
        field.valueKey,
      );
      updateTargetField(fieldIndex, {
        labelKey,
        previewOptions: opts,
        variableName: selectedNodeId, // Store node ID as variableName for v2.0
        selected_item: selectedDataIndex, // Store selected data index
      });
      return;
    }

    // For mesh version 1.0, use definedVariables
    if (!field.bundleId || !field.variableName || !field.valueKey) {
      updateTargetField(fieldIndex, { labelKey });
      return;
    }

    // Generate preview options
    const variable = definedVariables?.find(
      (v: any) =>
        String(v.sourceBundleId) === String(field.bundleId) &&
        v.name === field.variableName,
    );

    if (variable && field.valueKey) {
      const opts = generateDropdownOptions(
        variable.value,
        labelKey,
        field.valueKey,
      );
      updateTargetField(fieldIndex, {
        labelKey,
        previewOptions: opts,
      });
    } else {
      updateTargetField(fieldIndex, { labelKey });
    }
  };

  //   Handle value key change for specific target field
  const handleTargetFieldValueKeyChange = (
    fieldIndex: number,
    valueKey: string,
  ) => {
    const field = targetFields[fieldIndex];

    // For mesh version 2.0, use workflowData
    if (meshVersion === "2.0") {
      if (
        !field.bundleId ||
        !workflowData ||
        !selectedDataIndex ||
        !field.labelKey
      ) {
        updateTargetField(fieldIndex, { valueKey });
        return;
      }

      // Get the selected data item
      let selectedData;
      if (Array.isArray(workflowData)) {
        selectedData = workflowData[parseInt(selectedDataIndex)];
      } else {
        const keys = Object.keys(workflowData);
        selectedData = workflowData[keys[parseInt(selectedDataIndex)]];
      }

      const opts = generateDropdownOptions(
        selectedData,
        field.labelKey,
        valueKey,
      );
      updateTargetField(fieldIndex, {
        valueKey,
        previewOptions: opts,
        variableName: selectedNodeId, // Store node ID as variableName for v2.0
        selected_item: selectedDataIndex, // Store selected data index
      });
      return;
    }

    // For mesh version 1.0, use definedVariables
    if (!field.bundleId || !field.variableName || !field.labelKey) {
      updateTargetField(fieldIndex, { valueKey });
      return;
    }

    // Generate preview options
    const variable = definedVariables?.find(
      (v: any) =>
        String(v.sourceBundleId) === String(field.bundleId) &&
        v.name === field.variableName,
    );

    if (variable && field.labelKey) {
      const opts = generateDropdownOptions(
        variable.value,
        field.labelKey,
        valueKey,
      );
      updateTargetField(fieldIndex, {
        valueKey,
        previewOptions: opts,
      });
    } else {
      updateTargetField(fieldIndex, { valueKey });
    }
  };

  //  Toggle specific target field's children
  const toggleTargetFieldChildren = (fieldIndex: number) => {
    setExpandedTargetFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldIndex)) {
        newSet.delete(fieldIndex);
      } else {
        newSet.add(fieldIndex);
      }
      return newSet;
    });
  };

  //  Generate child nodes from preview options WITH complete nested data
  const generateChildNodesFromPreviewOptions = (field: any): DropdownNode[] => {
    if (!field.previewOptions || field.previewOptions.length === 0) {
      return [];
    }

    return field.previewOptions.map((opt: any) => {
      const node = {
        label: opt.label || "",
        value: opt.value || "",
        target: opt.target || "",
        bundleId: opt.bundleId || field.bundleId || "",
        variableName: opt.variableName || field.variableName || "",
        labelKey: opt.labelKey || field.labelKey || "",
        valueKey: opt.valueKey || field.valueKey || "",
        availableKeys: opt.availableKeys || field.availableKeys || [],
        isDefaultOption: opt.isDefaultOption || false,
        targetFields: opt.targetFields || [],
        children: opt.children || [],
      };

      return node;
    });
  };

  const handleAddDefaultDropdownOption = (fieldIndex: number) => {
    const labelInput = document.getElementById(
      `dl-${node.value}-${fieldIndex}`,
    ) as HTMLInputElement;
    const valueInput = document.getElementById(
      `dv-${node.value}-${fieldIndex}`,
    ) as HTMLInputElement;

    if (!labelInput || !valueInput) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Input fields not found.",
      });
      return;
    }

    const l = labelInput.value.trim();
    const v = valueInput.value.trim();

    if (l && v) {
      const formattedValue = v.toLowerCase().replace(/\s+/g, "_");
      addDefaultOption(fieldIndex, { label: l, value: formattedValue });
      labelInput.value = "";
      valueInput.value = "";
    } else {
      toast({
        variant: "destructive",
        title: "Missing Values",
        description: "Please provide both label and value.",
      });
    }
  };

  const [commonApiCallEnabled, setCommonApiCallEnabled] = React.useState<
    Record<number, boolean>
  >({});

  //   Toggle common API call for a specific target field
  const toggleCommonApiCall = (fieldIndex: number) => {
    setCommonApiCallEnabled((prev) => ({
      ...prev,
      [fieldIndex]: !prev[fieldIndex],
    }));
  };

  //   Get parent field info for common mode
  const getParentFieldInfo = () => {
    // This is a common node, extract parent info from node label
    if (node.label.startsWith("Common for ")) {
      const fieldName = node.label.replace("Common for ", "");
      return { fieldName, isCommon: true };
    }
    return { fieldName: "", isCommon: false };
  };

  const parentInfo = getParentFieldInfo();

  return (
    <>
      <div className=" border-lightGray rounded-lg p-4 mb-4 bg-muted shadow-sm border-2">
        <PageLoader show={loadingFieldIndex !== null} />
        {/* ========== HEADER: Option Name or Common Mode Info ========== */}
        {!node.label.startsWith("Common for") ? (
          // Regular Option Header
          <div className="flex items-center justify-between gap-2 pb-2 border-b  border-lightGray">
            <div className="flex-1">
              <label className="block text-sm font-medium">
                Option (Display Name)
                {isNested && (
                  <span className="text-xs text-muted-foreground ml-2">
                    (From parent preview options)
                  </span>
                )}
              </label>
              <input
                value={node.label}
                onChange={(e) =>
                  applyNodeUpdate({
                    label: e.target.value,
                    value: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                  })
                }
                className={`w-[320px] border border-lightGray rounded-md px-3 py-2 transition ${
                  isNested ? "bg-muted cursor-not-allowed opacity-70" : ""
                }`}
                placeholder="Enter option label"
                spellCheck={false}
                disabled={isNested}
                readOnly={isNested}
              />
            </div>
            <button
              onClick={onDelete}
              className="mt-7 p-2 text-primary hover:text-muted rounded-md transition"
              title="Delete option"
            >
              <Trash size={20} />
            </button>
          </div>
        ) : (
          // Common Mode Header
          <div className="flex items-center justify-between gap-4 pb-4 border-b border-lightGray -m-4 mb-4 p-4 rounded-t-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base font-semibold text-primaryBlue">
                  Common Configuration: {parentInfo.fieldName}
                </span>
                <InfoTooltip text="This configuration will apply to ALL options. Any logic you set here will be applied when any of these options is selected." />
              </div>

              <div className="bg-background border border-lightGray rounded-md p-3">
                <p className="text-xs font-medium mb-2">
                  Applies to these options:
                </p>
                <div className="flex flex-wrap gap-2">
                  {node.applicableOptions &&
                  node.applicableOptions.length > 0 ? (
                    node.applicableOptions.map((opt: any, idx: number) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-primaryBlue text-xs font-medium border border-lightGray"
                      >
                        {opt.label}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-muted italic">
                      All options
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onDelete}
              className="p-2 text-primary hover:text-muted rounded-md transition"
              title="Remove common configuration"
            >
              <Trash size={20} />
            </button>
          </div>
        )}

        {/* ========== TARGET FIELDS SECTION ========== */}
        <div className="mt-4 ml-6">
          {/* Section Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-lightBlue to-lightBlue px-4 py-3 rounded-t-lg border border-lightGray">
            <span className="text-sm font-semibold text-primaryBlue">
              Target Field{targetFields.length > 1 ? "s" : ""} of{" "}
              <span className="text-foreground">{node.label}</span>
            </span>
            <div className="flex items-center gap-2">
              <div>
                {targetFields.length > 0 && node.label && (
                  <span className="text-muted-foreground flex items-center gap-1 text-xs">
                    <span>
                      total targets of <strong>{node.label}</strong> is{" "}
                    </span>
                    <div className="w-5 h-5 rounded-full bg-primary text-background flex items-center justify-center">
                      {targetFields.length}
                    </div>
                  </span>
                )}
              </div>

              <button
                onClick={addTargetField}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primaryBlue hover:text-foreground rounded-md transition"
              >
                <CirclePlus size={16} />
                Add Target Field
              </button>
            </div>
          </div>

          {/* Target Fields List */}
          <div className="border border-lightBlue border-t-0 rounded-b-lg p-4 bg-extraLightGray space-y-4">
            {targetFields.length === 0 ? (
              <p className="text-sm text-primaryBlue text-center py-6">
                No target fields yet. Click <strong>Add Target Field</strong> to
                create one.
              </p>
            ) : (
              targetFields.map((field, fieldIndex) => (
                <div
                  key={fieldIndex}
                  className="bg-background border border-lightGray rounded-lg p-4 shadow-sm hover:shadow-md transition"
                >
                  {/* ========== FIELD HEADER ========== */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-lightGray ">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 bg-lightBlue text-primaryBlue text-xs font-bold rounded-full">
                        {fieldIndex + 1}
                      </span>
                      <span className="text-sm font-semibold text-primaryBlue">
                        {field.fieldName || `Target Field #${fieldIndex + 1}`}
                      </span>
                    </div>
                    <button
                      onClick={() => removeTargetField(fieldIndex)}
                      className="p-1.5 text-primary hover:text-muted rounded-md transition"
                      title="Remove field"
                    >
                      <Trash size={16} />
                    </button>
                  </div>

                  {/* ========== FIELD NAME & SOURCE TYPE ========== */}
                  <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-gray-700">
                          Target Field Name
                        </label>
                        <div className="flex items-center gap-1">
                          <InfoTooltip text="This field name will be used as the variable key. Click the copy button to copy the transformed value (lowercase with underscores)." />
                          <button
                            onClick={() => {
                              const transformedValue = field.fieldName
                                .toLowerCase()
                                .replace(/\s+/g, "_");
                              navigator.clipboard.writeText(transformedValue);
                              toast({
                                title: "Copied!",
                                description: `Copied "${transformedValue}" to clipboard`,
                                duration: 2000,
                              });
                            }}
                            disabled={!field.fieldName}
                            className="p-1 text-primaryBlue hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed rounded transition"
                            title="Copy transformed value"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                      <input
                        className="w-full border border-lightGray rounded-md px-3 py-2 text-sm "
                        placeholder="enter target field name (eg. result)"
                        value={field.fieldName}
                        onChange={(e) =>
                          updateTargetField(fieldIndex, {
                            fieldName: e.target.value,
                          })
                        }
                        spellCheck={false}
                      />
                    </div>

                    <div className="col-span-4 flex items-end">
                      <label className="flex items-center gap-2 px-3 py-2 bg-extraLightGray rounded-md border border-lightGray cursor-pointer hover:bg-lightGray transition">
                        <input
                          type="checkbox"
                          id={`source-type-${fieldIndex}`}
                          checked={field.sourceType === "default"}
                          onChange={(e) =>
                            updateTargetField(fieldIndex, {
                              sourceType: e.target.checked
                                ? "default"
                                : "bundle",
                              bundleId: "",
                              variableName: "",
                              labelKey: "",
                              valueKey: "",
                              availableKeys: [],
                              defaultOptions: [],
                              previewOptions: [],
                            })
                          }
                          className="w-4 h-4 text-primaryBlue rounded focus:ring-2"
                        />
                        <span className="text-sm font-medium">
                          Use Default Options
                        </span>
                      </label>
                      {field.fieldName && (
                        <p className="text-xs text-muted-foreground mt-1 ml-3 pb-3">
                          Will be stored as:{" "}
                          <code className="bg-muted px-1 py-0.5 rounded">
                            {field.fieldName.toLowerCase().replace(/\s+/g, "_")}
                          </code>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ========== DEFAULT OPTIONS MODE ========== */}
                  {field.sourceType === "default" && (
                    <div className="bg-bgAmber border border-amberBorder rounded-lg p-4">
                      <h5 className="text-xs font-semibold text-amber mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber rounded-full"></span>
                        Manual Options
                      </h5>

                      <div className="grid grid-cols-12 gap-3">
                        {/* Input Section */}
                        <div className="col-span-7 space-y-3">
                          <div>
                            <label
                              htmlFor={`dl-${node.value}-${fieldIndex}`}
                              className="block text-xs font-medium mb-1"
                            >
                              Display Label
                            </label>
                            <input
                              id={`dl-${node.value}-${fieldIndex}`}
                              placeholder="e.g. Block"
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              spellCheck={false}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor={`dv-${node.value}-${fieldIndex}`}
                              className="block text-xs font-medium mb-1"
                            >
                              Value
                            </label>
                            <input
                              id={`dv-${node.value}-${fieldIndex}`}
                              placeholder="e.g. block"
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              spellCheck={false}
                            />
                          </div>

                          <Button
                            size="sm"
                            onClick={() =>
                              handleAddDefaultDropdownOption(fieldIndex)
                            }
                            className="w-full bg-primaryAmber"
                          >
                            <CirclePlus size={14} className="mr-1" />
                            Add Option
                          </Button>
                        </div>

                        {/* Preview Section */}
                        <div className="col-span-5">
                          <label className="block text-xs font-medium mb-1">
                            Added Options ({field.previewOptions?.length || 0})
                          </label>
                          {field.previewOptions &&
                          field.previewOptions.length > 0 ? (
                            <div className="border border-amberBorder rounded-md max-h-40 overflow-auto">
                              <ul className="divide-y divide-lightGray bg-background">
                                {field.previewOptions.map(
                                  (opt: any, idx: number) => (
                                    <li
                                      key={idx}
                                      className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 transition group"
                                    >
                                      <span className="text-xs  truncate flex-1">
                                        {opt.label}
                                      </span>
                                      <button
                                        onClick={() =>
                                          handleDeletePreviewOption(
                                            fieldIndex,
                                            idx,
                                          )
                                        }
                                        className="ml-2 p-1 text-primary group-hover:opacity-100 transition"
                                      >
                                        <Delete size={14} />
                                      </button>
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          ) : (
                            <div className="border border-dashed border-amberBorder rounded-md bg-background p-4 text-center">
                              <p className="text-xs text-muted-foreground">
                                No options added yet
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ========== BUNDLE SOURCE MODE ========== */}
                  {field.sourceType === "bundle" && (
                    <div className="bg-lightGreen border border-greenBorder rounded-lg p-4">
                      <h5 className="text-xs font-semibold text-primaryGreen mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-primaryGreen rounded-full"></span>
                        Dynamic Options from{" "}
                        {meshVersion === "2.0" ? "Workflow" : "Bundle"}
                      </h5>

                      <div className="grid grid-cols-12 gap-3">
                        {/* Bundle/Workflow Selection */}
                        <div className="col-span-2">
                          <label className="block text-xs font-medium mb-1.5">
                            {meshVersion === "2.0"
                              ? "Select Workflow"
                              : "Select Bundle"}
                          </label>
                          <Select
                            value={field.bundleId || ""}
                            onValueChange={(v) =>
                              handleTargetFieldBundleChange(fieldIndex, v)
                            }
                          >
                            <SelectTrigger className="bg-background border-greenBorder">
                              <SelectValue
                                placeholder={
                                  meshVersion === "2.0"
                                    ? "Select Workflow"
                                    : "Select Bundle"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="none">
                                  Select a Workflow
                                </SelectItem>
                                {actionBundles?.map((b) => (
                                  <SelectItem
                                    key={b.bundleId || b.id}
                                    value={String(b.bundleId || b.id)}
                                  >
                                    {b.bundleTitle ||
                                      b.title ||
                                      `Bundle ${b.bundleId || b.id}`}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Variable/Data Selection */}
                        {meshVersion !== "2.0" && (
                          <div className="col-span-2">
                            <label className="block text-xs font-medium  mb-1.5">
                              Variable
                            </label>
                            <Select
                              value={field.variableName || ""}
                              onValueChange={(v) =>
                                handleTargetFieldVariableChange(fieldIndex, v)
                              }
                              disabled={!field.bundleId}
                            >
                              <SelectTrigger className="bg-background border-greenBorder">
                                <SelectValue placeholder="Select Variable" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {definedVariables
                                    ?.filter(
                                      (v) =>
                                        String(v.sourceBundleId) ===
                                        String(field.bundleId),
                                    )
                                    .map((v) => (
                                      <SelectItem key={v.name} value={v.name}>
                                        {v.name}
                                      </SelectItem>
                                    ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Choose Node (Mesh Version 2.0) */}
                        {meshVersion === "2.0" &&
                          field.bundleId &&
                          workflowNodeResults.length > 0 && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium mb-1.5">
                                Choose the Node
                              </label>
                              <Select
                                value={selectedNodeId}
                                onValueChange={(nodeId) => {
                                  setSelectedNodeId(nodeId);
                                  // Find and store the node's label
                                  const selectedNode = workflowNodeResults.find(
                                    (nr: any) => nr.uid === nodeId,
                                  );
                                  const nodeLabel =
                                    selectedNode?.header?.label || "";

                                  // Update the field with node metadata
                                  const fieldIndexToUpdate =
                                    targetFields.findIndex(
                                      (field) =>
                                        field.bundleId &&
                                        field.sourceType === "bundle",
                                    );
                                  if (fieldIndexToUpdate !== -1) {
                                    updateTargetField(fieldIndexToUpdate, {
                                      nodeId: nodeId,
                                      nodeLabel: nodeLabel,
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="bg-background border-greenBorder">
                                  <SelectValue placeholder="Select a node" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {workflowNodeResults
                                      .filter((nr: any) => {
                                        // exclude start and configuration nodes
                                        const excludeNodes = [
                                          "manual_trigger",
                                          "config_node",
                                        ];
                                        const sub_type = (
                                          nr.sub_type || ""
                                        ).toLowerCase();
                                        return !excludeNodes.some((node) =>
                                          sub_type.includes(node),
                                        );
                                      })
                                      .map((nr: any) => {
                                        const nodeLabel = nr.label || "";
                                        return (
                                          <SelectItem
                                            key={nr.uid}
                                            value={nr.uid}
                                          >
                                            {nr.title || "Untitled"}{" "}
                                            {nodeLabel ? ` (${nodeLabel})` : ""}
                                          </SelectItem>
                                        );
                                      })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                        {/* Data Selection (Mesh Version 2.0) */}
                        {meshVersion === "2.0" &&
                          field.bundleId &&
                          selectedNodeId && (
                            <div className="col-span-2">
                              <label className="flex items-center justify-between text-xs font-medium mb-1.5">
                                <span>Workflow Data</span>
                                {workflowData && (
                                  <span className="flex items-center gap-1 text-primaryGreen">
                                    <CheckCheck size={14} />
                                  </span>
                                )}
                              </label>
                              {loadingFieldIndex === fieldIndex ? (
                                <div className="bg-background border border-greenBorder rounded-md px-3 py-2 text-sm">
                                  <span className="text-muted-foreground flex items-center gap-2">
                                    <span className="animate-spin">
                                      <RotateCw size={14} />
                                    </span>{" "}
                                    Loading data...
                                  </span>
                                </div>
                              ) : !workflowData ? (
                                <div className="bg-background border border-greenBorder rounded-md px-3 py-2 text-sm">
                                  <span className="text-muted-foreground">
                                    Select a node to load data
                                  </span>
                                </div>
                              ) : (
                                <Select
                                  value={selectedDataIndex}
                                  onValueChange={(v) => setSelectedDataIndex(v)}
                                >
                                  <SelectTrigger className="bg-background border-greenBorder">
                                    <SelectValue placeholder="Select Data Item" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {(() => {
                                        if (Array.isArray(workflowData)) {
                                          return workflowData.map(
                                            (item: any, index: number) => (
                                              <SelectItem
                                                key={index}
                                                value={String(index)}
                                              >
                                                Item {index + 1}{" "}
                                                {item?.title ||
                                                  item?.name ||
                                                  ""}
                                              </SelectItem>
                                            ),
                                          );
                                        } else {
                                          // If it's an object, show its keys
                                          return Object.keys(workflowData).map(
                                            (key: string, index: number) => (
                                              <SelectItem
                                                key={index}
                                                value={String(index)}
                                              >
                                                {key}
                                              </SelectItem>
                                            ),
                                          );
                                        }
                                      })()}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}

                        {/* Keys Selection */}
                        {field.availableKeys &&
                          field.availableKeys.length > 0 && (
                            <div
                              className={
                                meshVersion === "2.0"
                                  ? "col-span-3"
                                  : "col-span-4"
                              }
                            >
                              <label className="block text-xs font-medium mb-1.5">
                                Keys (Label / Value)
                              </label>
                              <div className="flex gap-2">
                                <Select
                                  value={field.labelKey || ""}
                                  onValueChange={(val) =>
                                    handleTargetFieldLabelKeyChange(
                                      fieldIndex,
                                      val,
                                    )
                                  }
                                >
                                  <SelectTrigger className="bg-background border-greenBorder">
                                    <SelectValue placeholder="Label" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.availableKeys.map((k: string) => (
                                      <SelectItem key={k} value={k}>
                                        {k}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={field.valueKey || ""}
                                  onValueChange={(val) =>
                                    handleTargetFieldValueKeyChange(
                                      fieldIndex,
                                      val,
                                    )
                                  }
                                >
                                  <SelectTrigger className="bg-background border-greenBorder">
                                    <SelectValue placeholder="Value" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.availableKeys.map((k: string) => (
                                      <SelectItem key={k} value={k}>
                                        {k}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        {/* Preview Section */}
                        {field.previewOptions &&
                          field.previewOptions.length > 0 && (
                            <div
                              className={
                                meshVersion === "2.0"
                                  ? "col-span-3"
                                  : "col-span-4"
                              }
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-medium">
                                  Preview ({field.previewOptions.length})
                                </label>
                                <InfoTooltip
                                  text={`These options will be shown in "${field.fieldName}" when "${node.label}" is selected`}
                                />
                              </div>
                              <div className="border border-greenBorder rounded-md bg-background max-h-32 overflow-auto">
                                <ul className="divide-y divide-lightGray">
                                  {field.previewOptions.map(
                                    (opt: any, idx: number) => (
                                      <li
                                        key={idx}
                                        className="flex items-start justify-between gap-2 px-3 py-2 transition group"
                                      >
                                        <span className="text-xs flex-1 min-w-0 whitespace-normal break-all leading-5">
                                          {opt.label}
                                        </span>
                                        <button
                                          onClick={() =>
                                            handleDeletePreviewOption(
                                              fieldIndex,
                                              idx,
                                            )
                                          }
                                          className="ml-2 p-1 text-primary transition shrink-0"
                                        >
                                          <Delete size={14} />
                                        </button>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {/* ========== NESTED OPTIONS TOGGLE ========== */}
                  {field.fieldName &&
                    field.previewOptions &&
                    field.previewOptions.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-lightGray">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            toggleTargetFieldChildren(fieldIndex);
                          }}
                          className="w-fit border-primaryBlue text-primaryBlueflex items-center justify-center gap-2 bg-lightBlue"
                        >
                          {expandedTargetFields.has(fieldIndex) ? (
                            <>
                              <ChevronUp size={16} />
                              <span>
                                Hide Nested Options for {field.fieldName}
                              </span>
                            </>
                          ) : (
                            <>
                              <ChevronDown size={16} />
                              <span>
                                Show Nested Options for {field.fieldName}
                              </span>
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                  {/* ========== NESTED OPTIONS CONTENT ========== */}
                  {expandedTargetFields.has(fieldIndex) &&
                    field.previewOptions &&
                    field.previewOptions.length > 0 && (
                      <div className="mt-4 pl-6 border-l-4 border-lightBlue rounded-r-lg p-4 bg-extraLightGray">
                        {/* Common API Toggle */}
                        <div className="mb-4">
                          <label className="flex items-center gap-3 p-3 bg-background border border-lightBlue rounded-lg cursor-pointer\ transition">
                            <input
                              type="checkbox"
                              id={`common-api-${fieldIndex}`}
                              checked={
                                commonApiCallEnabled[fieldIndex] || false
                              }
                              onChange={() => toggleCommonApiCall(fieldIndex)}
                              className="w-4 h-4 text-primaryBlue rounded focus:ring-2 focus:ring-primaryBlue"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-primaryBlue">
                                Use Common API Call
                              </span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Single configuration for all options
                              </p>
                            </div>
                            <InfoTooltip text="Creates a single nested configuration applied to all options. More efficient than individual configurations." />
                          </label>
                        </div>

                        {/* Nested Content */}
                        {commonApiCallEnabled[fieldIndex] ? (
                          // Common Mode
                          <div className="bg-background border border-lightBlue rounded-lg p-4">
                            <DropdownNodeEditor
                              node={{
                                label: `Common for ${field.fieldName}`,
                                value: `common_${field.fieldName}`,
                                target: "",
                                bundleId: field.bundleId || "",
                                variableName: field.variableName || "",
                                labelKey: field.labelKey || "",
                                valueKey: field.valueKey || "",
                                availableKeys: field.availableKeys || [],
                                isDefaultOption: false,
                                targetFields:
                                  field.commonApiConfig?.targetFields || [],
                                children: [],
                                applicableOptions: field.previewOptions,
                              }}
                              onDelete={() => toggleCommonApiCall(fieldIndex)}
                              onUpdate={(updatedChild) => {
                                const updated = [...targetFields];
                                updated[fieldIndex].commonApiConfig =
                                  updatedChild;

                                if (updated[fieldIndex].previewOptions) {
                                  updated[fieldIndex].previewOptions = updated[
                                    fieldIndex
                                  ].previewOptions.map((opt: any) => ({
                                    ...opt,
                                    targetFields: updatedChild.targetFields,
                                    children: updatedChild.children,
                                    isCommonConfig: true,
                                    commonConfigRef: field.fieldName,
                                  }));
                                }

                                setTargetFields(updated);
                                sync({ targetFields: updated });
                              }}
                              actionBundles={actionBundles}
                              definedVariables={definedVariables}
                              extractKeysFromData={extractKeysFromData}
                              generateDropdownOptions={generateDropdownOptions}
                              prefillData={field.commonApiConfig}
                              meshVersion={meshVersion}
                              pluginId={pluginId}
                            />
                          </div>
                        ) : (
                          // Individual Mode
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-primaryBlue flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-primaryBlue rounded-full"></span>
                              Individual Configurations
                            </h4>
                            {generateChildNodesFromPreviewOptions(field).map(
                              (childNode, childIdx) => {
                                const previewOptionData =
                                  field.previewOptions?.[childIdx];
                                const completePrefillData = {
                                  label:
                                    previewOptionData?.label ||
                                    childNode.label ||
                                    "",
                                  value:
                                    previewOptionData?.value ||
                                    childNode.value ||
                                    "",
                                  bundleId:
                                    previewOptionData?.bundleId ||
                                    childNode.bundleId ||
                                    field.bundleId ||
                                    "",
                                  variableName:
                                    previewOptionData?.variableName ||
                                    childNode.variableName ||
                                    field.variableName ||
                                    "",
                                  labelKey:
                                    previewOptionData?.labelKey ||
                                    childNode.labelKey ||
                                    field.labelKey ||
                                    "",
                                  valueKey:
                                    previewOptionData?.valueKey ||
                                    childNode.valueKey ||
                                    field.valueKey ||
                                    "",
                                  availableKeys:
                                    previewOptionData?.availableKeys ||
                                    childNode.availableKeys ||
                                    field.availableKeys ||
                                    [],
                                  target:
                                    previewOptionData?.target ||
                                    childNode.target ||
                                    "",
                                  isDefaultOption:
                                    previewOptionData?.isDefaultOption ||
                                    childNode.isDefaultOption ||
                                    false,
                                  targetFields:
                                    previewOptionData?.targetFields ||
                                    childNode.targetFields ||
                                    [],
                                  children:
                                    previewOptionData?.children ||
                                    childNode.children ||
                                    [],
                                };

                                return (
                                  <DropdownNodeEditor
                                    key={`${fieldIndex}-${childIdx}-${childNode.value}`}
                                    node={childNode}
                                    isNested={true}
                                    onDelete={() =>
                                      handleDeletePreviewOption(
                                        fieldIndex,
                                        childIdx,
                                      )
                                    }
                                    onUpdate={(updatedChild) => {
                                      const updated = [...targetFields];
                                      if (updated[fieldIndex].previewOptions) {
                                        updated[fieldIndex].previewOptions[
                                          childIdx
                                        ] = {
                                          label: updatedChild.label,
                                          value: updatedChild.value,
                                          bundleId: updatedChild.bundleId || "",
                                          variableName:
                                            updatedChild.variableName || "",
                                          labelKey: updatedChild.labelKey || "",
                                          valueKey: updatedChild.valueKey || "",
                                          availableKeys:
                                            updatedChild.availableKeys || [],
                                          target: updatedChild.target || "",
                                          isDefaultOption:
                                            updatedChild.isDefaultOption ||
                                            false,
                                          targetFields:
                                            updatedChild.targetFields || [],
                                          children: updatedChild.children || [],
                                        };
                                        setTargetFields(updated);
                                        sync({ targetFields: updated });
                                      }
                                    }}
                                    actionBundles={actionBundles}
                                    definedVariables={definedVariables}
                                    extractKeysFromData={extractKeysFromData}
                                    generateDropdownOptions={
                                      generateDropdownOptions
                                    }
                                    prefillData={completePrefillData}
                                    meshVersion={meshVersion}
                                    pluginId={pluginId}
                                  />
                                );
                              },
                            )}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DropdownNodeEditor;
